import React, { useCallback, useContext, useMemo, useState } from 'react';
import Tooltip from 'components/Tooltip';
import AccountPickerContext from 'contexts/AccountPickerContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import UsersContext from 'contexts/UsersContext';
import { BonusSubAppBoardSignOffsContext } from 'contextProviders/BonusAppProvider/BonusSubAppBoardSignOffsProvider';
import userTypeCheckers from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/UsersProvider/userTypeCheckers';
import getTimeStamp from 'getTimeStamp';
import isoDateToAmerican from 'isoDateToAmerican';
import KingpinBadge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import useConfirmation from 'screens/DataManager/DatasetDefinitions/DatasetsIndex/PerformanceDatasetWizard/useConfirmation';
import STORE from 'store';

type SignOffBoardNavItem =
  | V3.Navigation.SignOffBoard
  | V3.Navigation.ApprovalsSignOffBoardTab;

const getSignOffId = ({
  period,
  signOffBoardNavItem,
}: {
  period: BonusTypes.BonusPeriod;
  signOffBoardNavItem: SignOffBoardNavItem;
}) => `${period.id}-${signOffBoardNavItem.boardId}`;

const SignOffButton = ({
  period,
  signOffBoardNavItem,
  app,
  isCurrentUserOwner,
}: {
  period: BonusTypes.BonusPeriod;
  signOffBoardNavItem: SignOffBoardNavItem;
  app: V3.BonusApp;
  isCurrentUserOwner: boolean;
}) => {
  const { signOffLookup, isLoading: isLoadingSignOffs } = useContext(
    BonusSubAppBoardSignOffsContext,
  );
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { allUsers } = useContext(UsersContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { getConfirmation, ConfirmationModal } = useConfirmation({
    title: `Sign off ${signOffBoardNavItem.label} for ${period.label}`,
    confirmText: 'Sign Off',
  });

  const signOffForPeriod = useMemo(() => {
    return signOffLookup[signOffBoardNavItem.boardId];
  }, [signOffBoardNavItem.boardId, signOffLookup]);

  const onSignOffClicked = useCallback(async () => {
    if (isLoading) {
      return;
    }

    const isConfirmed = await getConfirmation();
    if (!isConfirmed) {
      return;
    }

    setIsLoading(true);
    const id = getSignOffId({ period, signOffBoardNavItem });
    const newSignOff: BonusTypes.BoardSignOff = {
      id,
      signedOffOn: getTimeStamp(),
      signedOffById: currentUserId,
      boardId: signOffBoardNavItem.boardId,
      periodId: period.id,
    };
    const newSignOffs: BonusTypes.BoardSignOff[] = (() => {
      if (app.signOffs.some((so) => so.id === newSignOff.id)) {
        return app.signOffs.map((so) => {
          if (so.id === newSignOff.id) {
            return newSignOff;
          }
          return so;
        });
      } else {
        return [...app.signOffs, newSignOff];
      }
    })();

    const newApp: V3.BonusApp = {
      ...app,
      signOffs: newSignOffs,
    };

    STORE.contentDistributions
      .getBonusAppsRef({
        accountId: selectedAccountId,
      })
      .doc(newApp.id)
      .set(newApp)
      .then(() => {
        setIsLoading(false);
      });
  }, [
    app,
    currentUserId,
    getConfirmation,
    isLoading,
    period,
    selectedAccountId,
    signOffBoardNavItem,
  ]);

  const signedOffBy = useMemo(() => {
    if (!signOffForPeriod) {
      return undefined;
    }

    return allUsers
      .filter(userTypeCheckers.isSignedUpOrPendingUser)
      .find((u) => u.id === signOffForPeriod.signedOffById);
  }, [allUsers, signOffForPeriod]);

  if (isLoadingSignOffs) {
    return null;
  }

  if (!signOffForPeriod) {
    if (isCurrentUserOwner) {
      return (
        <>
          <Button
            size={'Small'}
            type={'Primary'}
            onClick={onSignOffClicked}
            label={'Sign Off'}
            isLoading={isLoading}
          />
          {ConfirmationModal}
        </>
      );
    }

    return (
      <Tooltip
        content={'Only one of the the listed owners may sign this board off'}
      >
        <KingpinBadge badgeType={'Blue'} text={`Pending Sign Off`} />
      </Tooltip>
    );
  }

  return (
    <div>
      <Tooltip
        content={
          signedOffBy
            ? `Signed off by ${signedOffBy.displayName} on ${isoDateToAmerican(signOffForPeriod.signedOffOn)}`
            : undefined
        }
      >
        <KingpinBadge badgeType={'Default'} text={`Signed off`} />
      </Tooltip>
    </div>
  );
};

export default SignOffButton;
